import { HeroAnimation } from "./HeroAnimation";
import type { Theme } from "./theme";

export function Hero({ theme }: { theme: Theme }) {
  return (
    <section
      style={{
        position: "relative",
        height: "100vh",
        width: "100%",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        isolation: "isolate",
        // The section needs its own opaque background so mix-blend-mode on
        // the tagline has something to compose against (otherwise the
        // isolated stacking context is effectively transparent and the
        // blend short-circuits — text renders as its own colour everywhere).
        background: theme.bg,
      }}
    >
      <HeroAnimation theme={theme} />
      {/* iOS Safari doesn't reliably composite mix-blend-mode across a WebGL
          canvas layer, so the white tagline reads as white instead of inverting
          against the ribbon. The -webkit-touch-callout feature query matches
          iOS-only (WebKit on iOS); there we drop the blend and use the theme's
          foreground colour, which stays legible over both page bg and ribbon. */}
      <style>{`
        @supports (-webkit-touch-callout: none) {
          .rarion-hero-tagline,
          .rarion-hero-subtagline,
          .rarion-hero-tertiary {
            color: ${theme.fg} !important;
            mix-blend-mode: normal !important;
          }
        }
      `}</style>
      <div
        style={{
          // No z-index here — would create a nested stacking context and
          // short-circuit the tagline's mix-blend-mode. The canvas sits
          // above via position: absolute / DOM order, the text container
          // below via being later in the DOM; both stay in the section's
          // stacking context so the blend composites against the canvas.
          position: "relative",
          maxWidth: 1200,
          margin: "0 auto",
          padding: "0 clamp(20px, 5vw, 40px)",
          width: "100%",
        }}
      >
        <h1
          className="rarion-hero-tagline"
          style={{
            fontSize: "clamp(44px, 7vw, 112px)",
            lineHeight: 1.02,
            letterSpacing: "-2.4px",
            fontWeight: 700,
            // White + difference blend: over the white page bg the text
            // renders as black; over the ribbon it renders as the inverse
            // of the ribbon colour, so the text brightens where the ribbon
            // sits beneath it.
            color: "#ffffff",
            mixBlendMode: "difference",
            margin: 0,
            maxWidth: 1000,
            textWrap: "balance",
          }}
        >
          Make better hiring decisions.
        </h1>
        <p
          className="rarion-hero-subtagline"
          style={{
            fontSize: "clamp(18px, 2vw, 26px)",
            lineHeight: 1.35,
            letterSpacing: "-0.3px",
            fontWeight: 400,
            color: "#ffffff",
            mixBlendMode: "difference",
            margin: "28px 0 0",
            maxWidth: 820,
            textWrap: "balance",
          }}
        >
          A collaborative system built on executive search methodology and occupational psychology.
        </p>
        <p
          className="rarion-hero-tertiary"
          style={{
            fontSize: "clamp(20px, 2.2vw, 30px)",
            lineHeight: 1.3,
            letterSpacing: "-0.4px",
            fontWeight: 500,
            color: "#ffffff",
            mixBlendMode: "difference",
            margin: "20px 0 0",
            maxWidth: 820,
            textWrap: "balance",
          }}
        >
          AI where it helps. Human judgement where it matters.
        </p>
      </div>
    </section>
  );
}
